import { useRef, useState } from "react";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { generateCSV } from "utils/csv";
import { downloadFile } from "utils/downloads";
import { removeDuplicatedElements } from "utils/Array";
import {
  useAddGraduationClassMutation,
  useGetAcademicYearsByIdLazyQuery,
  useGetGraduationClassesByProgramLazyQuery,
  useGetProgramByIdLazyQuery,
} from "graphql/__generated__/hooks";

const COLUMNS = ["name", "program_id", "start_date", "academic_year"];
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type CohortData = {
  row_key: string;
  name: string;
  program_id: string;
  start_date: string;
  academic_year: string;
};

type LoadingData = { current: number; total: number };

type ReportData = {
  success: string[];
  error: string[];
  alreadyExists: string[];
};

export const useUploadCSVCohorts = () => {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [displayData, setDisplayData] = useState(false);
  const [data, setData] = useState<CohortData[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ReportData>({
    success: [],
    error: [],
    alreadyExists: [],
  });
  const selectedData = useRef<string[]>([]);
  const navigate = useNavigate();

  const [addGraduationClass] = useAddGraduationClassMutation();
  const [getProgramById] = useGetProgramByIdLazyQuery();
  const [getAcademicYearById] = useGetAcademicYearsByIdLazyQuery();
  const [getGraduationClassesByProgram] = useGetGraduationClassesByProgramLazyQuery();

  const handleContinue = (uploadedFile: File) => {
    if (!uploadedFile) return;
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target?.result) return;
      const csvData = event.target.result as string;

      Papa.parse(csvData, {
        header: true,
        complete: (results: any) => {
          const parsedData = (results.data || [])
            .filter((item: CohortData) => item.name)
            .map((item: CohortData) => ({
              row_key: `${item.name}-${item.program_id}-${item.start_date}-${item.academic_year}`,
              name: item.name?.trim() || "",
              program_id: item.program_id?.trim() || "",
              start_date: item.start_date?.trim() || "",
              academic_year: item.academic_year?.trim() || "",
            }));

          const parsedDataFiltered = removeDuplicatedElements(parsedData, "row_key");
          setData(parsedDataFiltered);
          selectedData.current = parsedDataFiltered.map((item) => item.row_key);
          setDisplayData(true);
        },
      });
    };

    reader.readAsText(uploadedFile);
  };

  const onSubmit = async () => {
    setError(null);
    setLoading(true);

    const selectedRows =
      selectedData.current.length > 0
        ? data.filter((row) => selectedData.current.includes(row.row_key))
        : data;

    const success: string[] = [];
    const errorRows: string[] = [];
    const alreadyExists: string[] = [];
    const existingByProgram = new Map<string, Set<string>>();

    setLoadingData({ current: 0, total: selectedRows.length });

    for (const row of selectedRows) {
      const rowLabel = `${row.name} (${row.start_date})`;

      if (!row.name || !row.program_id || !row.start_date || !row.academic_year) {
        errorRows.push(`${rowLabel} - missing required fields`);
        setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
        continue;
      }

      if (!UUID_REGEX.test(row.program_id) || !UUID_REGEX.test(row.academic_year)) {
        errorRows.push(`${rowLabel} - invalid UUID format`);
        setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
        continue;
      }

      try {
        const [{ data: programData }, { data: academicYearData }] = await Promise.all([
          getProgramById({ variables: { programId: row.program_id } }),
          getAcademicYearById({ variables: { academic_year_id: row.academic_year } }),
        ]);

        if (!programData?.program_by_pk) {
          errorRows.push(`${rowLabel} - program_id not found`);
          setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
          continue;
        }

        if (!academicYearData?.academic_year_by_pk) {
          errorRows.push(`${rowLabel} - academic_year not found`);
          setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
          continue;
        }

        const academicYearProgramId = academicYearData.academic_year_by_pk.program_id || "";
        if (academicYearProgramId !== row.program_id) {
          errorRows.push(
            `${rowLabel} - academic_year does not belong to provided program_id`,
          );
          setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
          continue;
        }

        if (!existingByProgram.has(row.program_id)) {
          const existingResult = await getGraduationClassesByProgram({
            variables: { programId: row.program_id },
          });
          const existingSet = new Set(
            (existingResult.data?.graduation_class || []).map(
              (cohort) => `${cohort.name.toLowerCase()}|${cohort.start_date || ""}`,
            ),
          );
          existingByProgram.set(row.program_id, existingSet);
        }

        const existingKey = `${row.name.toLowerCase()}|${row.start_date}`;
        if (existingByProgram.get(row.program_id)?.has(existingKey)) {
          alreadyExists.push(rowLabel);
          setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
          continue;
        }

        await addGraduationClass({
          variables: {
            name: row.name,
            program_id: row.program_id,
            start_date: row.start_date,
            current_academic_year_id: row.academic_year,
          },
        });
        success.push(rowLabel);
      } catch {
        errorRows.push(rowLabel);
      }

      setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
    }

    setReport({
      success,
      error: errorRows,
      alreadyExists,
    });
    setLoading(false);
    setCompleted(true);
  };

  const handleDownloadClick = () => {
    const csvContent = generateCSV(
      [
        {
          name: "Cohort 2026",
          program_id: "11111111-1111-1111-1111-111111111111",
          start_date: "2026-01-15",
          academic_year: "22222222-2222-2222-2222-222222222222",
        },
      ],
      COLUMNS,
    );
    downloadFile(csvContent, "cohort_example.csv", "text/csv;charset=utf-8;");
  };

  const handleOnComplete = () => {
    navigate(AdminRoutes.Home);
  };

  return {
    loading,
    completed,
    displayData,
    data,
    file,
    error,
    loadingData,
    report,
    selectedData,
    setDisplayData,
    setFile,
    handleContinue,
    handleDownloadClick,
    onSubmit,
    handleOnComplete,
  };
};
